import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { 
  Timer, 
  Plus, 
  Calendar, 
  BarChart3, 
  TrendingUp,
  ChevronLeft, 
  ChevronRight, 
  Loader2 
} from "lucide-react";
import Modal from "../../../components/admin/Modal";
import { useToast } from "../../../components/admin/ToastContext";
import { get, post } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

const dateKey = (d) => {
  if (typeof d === "string") return d.slice(0, 10);
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const toHours = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ESSTimesheetsPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const { user } = useSelector((state) => state.auth);
  const [entries, setEntries] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [weekHours, setWeekHours] = useState(0);
  const [monthHours, setMonthHours] = useState(0);
  const [form, setForm] = useState({ project_id: "", name: "", date: "", unit_amount: 0 });
  const PAGE_SIZE = 20;

  useEffect(() => {
    const load = async () => {
      try {
        const empRes = await get("/api/employees?limit=500");
        const allEmployees = empRes?.data || [];
        const currentEmp = allEmployees.find((e) => e.work_email === user?.email) || null;
        const empId = currentEmp?.id || currentEmp?.employee_id;
        setEmployeeId(empId);
        if (!empId) { setLoaded(true); return; }

        const tsRes = await get(`/api/timesheets?employee_id=${empId}`);
        const records = tsRes?.data || [];
        setTotalCount(records.length);
        setEntries(records);

        const projRes = await get("/api/projects");
        setProjects(projRes?.data || []);

        const now = new Date();
        const day = now.getDay();
        const monday = new Date(now);
        monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);

        const weekStartKey = dateKey(monday);
        const weekEndKey = dateKey(friday);
        const weekTotal = records
          .filter(r => {
            const k = dateKey(r.date);
            return k >= weekStartKey && k <= weekEndKey;
          })
          .reduce((s, r) => s + toHours(r.unit_amount), 0);
        setWeekHours(Math.round(weekTotal * 100) / 100);

        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const monthStartKey = dateKey(monthStart);
        const monthEndKey = dateKey(monthEnd);
        const monthTotal = records
          .filter(r => {
            const k = dateKey(r.date);
            return k >= monthStartKey && k <= monthEndKey;
          })
          .reduce((s, r) => s + toHours(r.unit_amount), 0);
        setMonthHours(Math.round(monthTotal * 100) / 100);
      } catch (_) {
        setEntries([]);
      }
      setLoaded(true);
    };
    load();
  }, [user]);

  const totalHours = entries.reduce((s, r) => s + toHours(r.unit_amount), 0);
  const totalPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  const paginated = entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, []);

  const projectName = (id) => {
    const p = (projects || []).find(p => p.id === id);
    return p ? p.name : id;
  };

  const handleLogTime = async () => {
    if (!form.project_id || !form.name.trim() || !form.date || !form.unit_amount) {
      addToast(intl.formatMessage({ id: "ess.common.fillAllFields" }), "error");
      return;
    }
    setSubmitting(true);
    try {
      await post("/api/timesheets", {
        employee_id: employeeId,
        project_id: parseInt(form.project_id),
        name: form.name.trim(),
        date: form.date,
        unit_amount: parseFloat(form.unit_amount) || 0,
      });
      addToast(intl.formatMessage({ id: "ess.timesheets.addSuccess" }), "success");
      setModalOpen(false);
      setForm({ project_id: "", name: "", date: "", unit_amount: 0 });
      window.location.reload();
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.timesheets.addError" }), "error");
    }
    setSubmitting(false);
  };

  if (!loaded) return <PageSkeleton />;

  const avgPerEntry = entries.length ? Math.round((totalHours / entries.length) * 100) / 100 : 0;

  const stats = [
    { label: intl.formatMessage({ id: "ess.timesheets.thisWeek" }), value: `${weekHours}h`, icon: Timer, color: "text-emerald-600 bg-emerald-100" },
    { label: intl.formatMessage({ id: "ess.timesheets.thisMonth" }), value: `${monthHours}h`, icon: Calendar, color: "text-blue-600 bg-blue-100" },
    { label: intl.formatMessage({ id: "ess.timesheets.total" }), value: `${totalHours}h`, icon: BarChart3, color: "text-purple-600 bg-purple-100" },
    { label: intl.formatMessage({ id: "ess.timesheets.avgPerDay" }), value: `${avgPerEntry}h`, icon: TrendingUp, color: "text-amber-600 bg-amber-100" },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {intl.formatMessage({ id: "ess.timesheets.title" })}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {intl.formatMessage({ id: "ess.timesheets.subtitle" })}
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-sm"
        >
          <Plus size={16} /> {intl.formatMessage({ id: "ess.timesheets.logTime" })}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">{intl.formatMessage({ id: "ess.timesheets.allEntries" }, { count: totalCount })}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.timesheets.colProject" })}</th>
                  <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden md:table-cell">{intl.formatMessage({ id: "ess.timesheets.colDescription" })}</th>
                  <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.timesheets.colDate" })}</th>
                  <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 text-right">{intl.formatMessage({ id: "ess.timesheets.colHours" })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-12 text-center text-gray-400">{intl.formatMessage({ id: "ess.timesheets.empty" })}</td></tr>
                ) : (
                  paginated.map((row, i) => (
                    <tr key={row.id || i} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5 font-semibold text-gray-900">
                        {Array.isArray(row.project_id) ? row.project_id[1] : projectName(row.project_id)}
                      </td>
                      <td className="px-5 py-3.5 text-gray-600 hidden md:table-cell max-w-[200px] truncate">
                        {row.name || "—"}
                      </td>
                       <td className="px-5 py-3.5 text-gray-700 font-medium">
                        {new Date(row.date).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-5 py-3.5 text-right font-semibold text-gray-900">
                        {row.unit_amount || 0}h
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>


          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30">
              <span className="text-xs text-gray-500">{intl.formatMessage({ id: "ess.common.pagination" }, { count: entries.length, page, total: totalPages })}</span>
              <div className="flex gap-1.5">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 transition-colors"
                ><ChevronLeft size={16} /></button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                      p === page ? "bg-emerald-500 text-white border-emerald-500" : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >{p}</button>
                ))}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 transition-colors"
                ><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">{intl.formatMessage({ id: "ess.timesheets.recent" })}</h3>
          <div className="space-y-4">
            {entries.slice(0, 5).map((row, i) => (
              <div key={row.id || i} className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-semibold text-gray-700 truncate block">
                    {Array.isArray(row.project_id) ? row.project_id[1] : intl.formatMessage({ id: "ess.timesheets.projectFallback" })}
                  </span>
                     <span className="text-xs text-gray-400">
                       {new Date(row.date).toLocaleDateString("fr-FR")}
                     </span>
                </div>
                <span className="text-sm font-bold text-gray-900 ml-2">{row.unit_amount || 0}h</span>
              </div>
            ))}
            {entries.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">{intl.formatMessage({ id: "ess.timesheets.noRecent" })}</p>
            )}
          </div>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={intl.formatMessage({ id: "ess.timesheets.modalTitle" })}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.timesheets.colProject" })}</label>
            <select
              value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white"
            >
              <option value="">{intl.formatMessage({ id: "ess.timesheets.selectProject" })}</option>
              {(projects || []).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.timesheets.colDescription" })}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              placeholder={intl.formatMessage({ id: "ess.timesheets.descPlaceholder" })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.timesheets.colDate" })}</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.timesheets.colHours" })}</label>
            <input
              type="number"
              min={0}
              max={24}
              step={0.25}
              value={form.unit_amount}
              onChange={(e) => setForm({ ...form, unit_amount: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              placeholder={intl.formatMessage({ id: "ess.timesheets.hoursPlaceholder" })}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setModalOpen(false)} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">{intl.formatMessage({ id: "ess.common.cancel" })}</button>
            <button onClick={handleLogTime} disabled={submitting} className="flex-1 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50">{submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> {intl.formatMessage({ id: "ess.common.sending" })}</> : intl.formatMessage({ id: "ess.common.add" })}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ESSTimesheetsPage;
